import React, { useState, useRef, useEffect } from 'react';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from '../exportUtils';

const ACCOUNTS = [
  'Cash', 'Accounts Receivable', 'Inventory', 'Fixed Assets',
  'Accounts Payable', 'Short-term Loans', 'Owner Equity',
  'Sales Revenue', 'Service Revenue', 'Cost of Goods Sold',
  'Salaries Expense', 'Rent Expense',
];

const SAMPLE_TRANSFERS = [
  { id: 'TRF-001', ref: '', date: '2026-02-01', from: 'Cash',         to: 'Accounts Receivable', amount: 3000, note: 'Payment received',  status: 'Completed' },
  { id: 'TRF-002', ref: '', date: '2026-02-05', from: 'Owner Equity', to: 'Cash',                 amount: 5000, note: 'Capital injection', status: 'Completed' },
  { id: 'TRF-003', ref: '', date: '2026-02-12', from: 'Cash',         to: 'Rent Expense',          amount: 1500, note: 'Monthly rent',      status: 'Pending'   },
];

const StatusBadge = ({ status }) => {
  const s = status === 'Completed'
    ? { background: '#dcfce7', color: '#166534' }
    : { background: '#fef9c3', color: '#854d0e' };
  return (
    <span style={{ ...s, padding: '0.25rem 0.7rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
      {status}
    </span>
  );
};

const inputStyle = {
  width: '100%',
  padding: '0.7rem 0.875rem',
  border: '1px solid #cbd5e1',
  borderRadius: '4px',
  fontSize: '0.9rem',
  outline: 'none',
  boxSizing: 'border-box',
  background: '#fff',
  color: '#1e293b',
};

const Transfer = () => {
  const [transfers, setTransfers]   = useState(SAMPLE_TRANSFERS);
  const [search, setSearch]         = useState('');
  const [showModal, setShowModal]   = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [form, setForm]             = useState({ ref: '', from: '', to: '', amount: '0', date: '', note: '' });
  const [errors, setErrors]         = useState({});
  const exportRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (exportRef.current && !exportRef.current.contains(e.target)) setShowExport(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = transfers.filter(t =>
    t.from.toLowerCase().includes(search.toLowerCase()) ||
    t.to.toLowerCase().includes(search.toLowerCase()) ||
    t.id.includes(search)
  );

  const openModal = () => {
    setForm({ ref: '', from: '', to: '', amount: '0', date: '', note: '' });
    setErrors({});
    setShowModal(true);
  };

  const validate = () => {
    const e = {};
    if (!form.from)                              e.from   = true;
    if (!form.to)                                e.to     = true;
    if (!form.amount || parseFloat(form.amount) <= 0) e.amount = true;
    if (!form.date)                              e.date   = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    setTransfers(prev => [
      {
        id:     `TRF-${String(prev.length + 1).padStart(3, '0')}`,
        ref:    form.ref,
        date:   form.date,
        from:   form.from,
        to:     form.to,
        amount: parseFloat(form.amount),
        note:   form.note,
        status: 'Completed',
      },
      ...prev,
    ]);
    setShowModal(false);
  };

  const handleExport = (format) => {
    if (!filtered.length) { alert('No data to export'); setShowExport(false); return; }
    const headers = ['Transfer #', 'Date', 'From Account', 'To Account', 'Amount', 'Note', 'Status'];
    const rows = filtered.map(t => [t.id, t.date, t.from, t.to, t.amount.toFixed(2), t.note || '', t.status]);
    if (format === 'CSV')   exportToCSV(headers, rows, 'transfers');
    if (format === 'Excel') exportToExcel(headers, rows, 'transfers');
    if (format === 'PDF')   exportToPDF('Fund Transfers', headers, rows, 'transfers');
    if (format === 'Print') printPage();
    setShowExport(false);
  };

  const totalAmount = transfers.reduce((s, t) => s + t.amount, 0);

  return (
    <div className="accounting-page">

      {/* ── Toolbar ── */}
      <div className="table-controls" style={{ marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div className="table-controls-left" />
        <div className="table-controls-right" style={{ gap: '0.75rem', flexWrap: 'wrap' }}>

          {/* Search */}
          <div style={{ position: 'relative' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
              style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              placeholder="Search transfers..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ height: '38px', paddingLeft: '2.2rem', paddingRight: '0.75rem', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.875rem', outline: 'none', minWidth: '220px' }}
            />
          </div>

          {/* Export */}
          <div ref={exportRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setShowExport(p => !p)}
              style={{ height: '38px', padding: '0 1rem', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.875rem', fontWeight: 600, color: '#475569', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Export ▾
            </button>
            {showExport && (
              <div style={{ position: 'absolute', right: 0, top: '110%', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 50, minWidth: '160px' }}>
                {['CSV', 'Excel', 'PDF', 'Print'].map(f => (
                  <div key={f} onClick={() => handleExport(f)}
                    style={{ padding: '0.6rem 1rem', cursor: 'pointer', fontSize: '0.875rem', color: '#334155', borderBottom: f !== 'Print' ? '1px solid #f1f5f9' : 'none' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    Export {f}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add Transfer */}
          <button
            onClick={openModal}
            style={{ height: '38px', padding: '0 1.25rem', background: 'var(--etpos-primary,#2563eb)', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '0.875rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>+</span> Add Transfer
          </button>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="acc-stats-row" style={{ marginBottom: '1rem' }}>
        {[
          { label: 'Total Transfers', value: transfers.length,                                       color: 'blue'   },
          { label: 'Completed',       value: transfers.filter(t => t.status === 'Completed').length,  color: 'green'  },
          { label: 'Pending',         value: transfers.filter(t => t.status === 'Pending').length,    color: 'orange' },
          { label: 'Total Amount',    value: `$${totalAmount.toLocaleString()}`,                      color: 'purple' },
        ].map(s => (
          <div key={s.label} className={`acc-stat-card ${s.color}`}>
            <div className="acc-stat-label">{s.label}</div>
            <div className="acc-stat-value">{s.value}</div>
          </div>
        ))}
      </div>

      {/* ── Table ── */}
      <div className="table-content-card">
        <h3 className="acc-card-title">Fund Transfers</h3>
        <div style={{ overflowX: 'auto' }}>
          <table className="products-table" style={{ minWidth: '680px' }}>
            <thead>
              <tr>
                <th>Transfer #</th>
                <th>Date</th>
                <th>From Account</th>
                <th>To Account</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th>Note</th>
                <th style={{ textAlign: 'center' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => (
                <tr key={t.id}>
                  <td><span style={{ fontWeight: 700, color: 'var(--etpos-primary,#2563eb)' }}>{t.id}</span></td>
                  <td style={{ fontSize: '0.85rem' }}>{t.date}</td>
                  <td>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <span style={{ color: '#ef4444', fontWeight: 700 }}>↑</span>{t.from}
                    </span>
                  </td>
                  <td>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <span style={{ color: '#10b981', fontWeight: 700 }}>↓</span>{t.to}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>
                    ${t.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </td>
                  <td style={{ color: '#64748b', fontSize: '0.85rem' }}>{t.note || '—'}</td>
                  <td style={{ textAlign: 'center' }}><StatusBadge status={t.status} /></td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🔄</div>
                    <div style={{ fontWeight: 600 }}>No transfers found</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Add Transfer Modal ── */}
      {showModal && (
        <div
          onClick={() => setShowModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: '8px',
              width: '95%',
              maxWidth: '540px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 25px 50px rgba(0,0,0,0.2)'
            }}
          >
            {/* Header */}
            <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#1e293b' }}>Add Transfer</h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>✕</button>
            </div>

            {/* Body — scrollable */}
            <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>

              {/* Reference No */}
              <div style={{ marginBottom: '1.1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#1e293b' }}>
                  Reference No:
                  <span title="Auto-generated if left blank" style={{ background: '#06b6d4', color: '#fff', borderRadius: '50%', width: '16px', height: '16px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, cursor: 'help', flexShrink: 0 }}>ℹ</span>
                </label>
                <input value={form.ref} onChange={e => setForm(p => ({ ...p, ref: e.target.value }))} style={inputStyle} />
              </div>

              {/* Transfer From */}
              <div style={{ marginBottom: '1.1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#1e293b' }}>
                  Transfer from:<span style={{ color: '#ef4444' }}>*</span>
                </label>
                <select
                  value={form.from}
                  onChange={e => setForm(p => ({ ...p, from: e.target.value }))}
                  style={{ ...inputStyle, border: errors.from ? '1px solid #ef4444' : '1px solid #cbd5e1' }}
                >
                  <option value="">Please Select</option>
                  {ACCOUNTS.map(a => <option key={a}>{a}</option>)}
                </select>
                {errors.from && <div style={{ color: '#ef4444', fontSize: '0.78rem', marginTop: '0.25rem' }}>Please select a source account</div>}
              </div>

              {/* Transfer To */}
              <div style={{ marginBottom: '1.1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#1e293b' }}>
                  Transfer To:<span style={{ color: '#ef4444' }}>*</span>
                </label>
                <select
                  value={form.to}
                  onChange={e => setForm(p => ({ ...p, to: e.target.value }))}
                  style={{ ...inputStyle, border: errors.to ? '1px solid #ef4444' : '1px solid #cbd5e1' }}
                >
                  <option value="">Please Select</option>
                  {ACCOUNTS.filter(a => a !== form.from).map(a => <option key={a}>{a}</option>)}
                </select>
                {errors.to && <div style={{ color: '#ef4444', fontSize: '0.78rem', marginTop: '0.25rem' }}>Please select a destination account</div>}
              </div>

              {/* Amount */}
              <div style={{ marginBottom: '1.1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#1e293b' }}>
                  Amount:<span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="number" min="0"
                  value={form.amount}
                  onChange={e => setForm(p => ({ ...p, amount: e.target.value }))}
                  style={{ ...inputStyle, border: errors.amount ? '1px solid #ef4444' : '1px solid #cbd5e1' }}
                />
                {errors.amount && <div style={{ color: '#ef4444', fontSize: '0.78rem', marginTop: '0.25rem' }}>Please enter a valid amount</div>}
              </div>

              {/* Date */}
              <div style={{ marginBottom: '1.1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#1e293b' }}>
                  Date:<span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="date"
                  value={form.date}
                  onChange={e => setForm(p => ({ ...p, date: e.target.value }))}
                  style={{ ...inputStyle, border: errors.date ? '1px solid #ef4444' : '1px solid #cbd5e1' }}
                />
                {errors.date && <div style={{ color: '#ef4444', fontSize: '0.78rem', marginTop: '0.25rem' }}>Please select a date</div>}
              </div>

              {/* Note */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#1e293b' }}>Note</label>
                <textarea
                  value={form.note}
                  onChange={e => setForm(p => ({ ...p, note: e.target.value }))}
                  placeholder="Note"
                  rows={4}
                  style={{ ...inputStyle, resize: 'vertical', minHeight: '90px', fontFamily: 'inherit' }}
                />
              </div>
            </div>

            {/* Footer — centered */}
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'center', gap: '0.75rem' }}>
              <button
                onClick={handleSubmit}
                style={{ background: '#5b21b6', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.55rem 2rem', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer' }}
                onMouseEnter={e => e.target.style.background = '#4c1d95'}
                onMouseLeave={e => e.target.style.background = '#5b21b6'}
              >
                Save
              </button>
              <button
                onClick={() => setShowModal(false)}
                style={{ background: '#334155', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.55rem 2rem', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer' }}
                onMouseEnter={e => e.target.style.background = '#1e293b'}
                onMouseLeave={e => e.target.style.background = '#334155'}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transfer;
